import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AlertsFilterCategory, AlertsFilters } from '@sternum/web/features/alerts/models';
import { AnomaliesFilterCategory, AnomaliesFilters } from '@sternum/web/features/anomalies/models';
import { AttackFilterField, AttackFilters } from '@sternum/web/features/attacks/models';
import { appStorage } from '@sternum/web/services/appStorage';
import { updateFiltersPerCategory, UpdateFiltersPerCategoryArgs } from '@sternum/web/utils';

interface TabAlerts {
  searchValue: string;
  isSidebarFilterOpen: boolean;
  selectedFiltersPerCategory: AlertsFilters;
}

interface TabInventory {
  isSidebarFilterOpen: boolean;
}

interface TabAttacks {
  searchValue: string;
  isSidebarFilterOpen: boolean;
  selectedFiltersPerCategory: AttackFilters;
}

interface TabAnomalies {
  searchValue: string;
  isSidebarFilterOpen: boolean;
  selectedFiltersPerCategory: AnomaliesFilters;
}

interface TabLogs {
  expandedRows: Record<string, boolean>;
  isFiltersPanelOpen: boolean;
}

export interface FleetsViewState {
  isPolling: boolean;
  tabAlerts: TabAlerts;
  tabInventory: TabInventory;
  tabAttacks: TabAttacks;
  tabAnomalies: TabAnomalies;
  tabLogs: TabLogs;
}

const getInitialState: () => FleetsViewState = () => {
  return {
    isPolling: false,
    tabAlerts: {
      searchValue: '',
      isSidebarFilterOpen: appStorage.fleetsView.getIsSidebarFilterOpenForTabAlerts() ?? false,
      selectedFiltersPerCategory: {},
    },
    tabInventory: {
      isSidebarFilterOpen: appStorage.fleetsView.getIsSidebarFilterOpenForTabInventory() ?? false,
    },
    tabAttacks: {
      searchValue: '',
      isSidebarFilterOpen: appStorage.fleetsView.getIsSidebarFilterOpenForTabAttacks() ?? false,
      selectedFiltersPerCategory: {},
    },
    tabAnomalies: {
      searchValue: '',
      isSidebarFilterOpen: appStorage.fleetsView.getIsSidebarFilterOpenForTabAnomalies() ?? false,
      selectedFiltersPerCategory: {},
    },
    tabLogs: {
      expandedRows: {},
      isFiltersPanelOpen: appStorage.fleetsView.getIsSidebarFilterOpenForTabLogs() ?? false,
    },
  };
};

export const fleetsViewSlice = createSlice({
  name: 'fleetsView',
  initialState: getInitialState,
  reducers: {
    toggleIsPollingAction(state, action: PayloadAction<boolean | undefined>) {
      state.isPolling = action.payload === undefined ? !state.isPolling : action.payload;
    },

    tabAlertsSetSearchValueAction(state, action: PayloadAction<string>) {
      state.tabAlerts.searchValue = action.payload;
      state.isPolling = false;
    },

    tabAlertsToggleIsSidebarFilterOpenAction(state, action: PayloadAction<boolean | undefined>) {
      state.tabAlerts.isSidebarFilterOpen =
        action.payload === undefined ? !state.tabAlerts.isSidebarFilterOpen : action.payload;
    },

    tabAlertsUpdateFiltersPerCategoryAction(
      state,
      action: PayloadAction<UpdateFiltersPerCategoryArgs<AlertsFilterCategory>>,
    ) {
      const categoryKey = action.payload.categoryKey;

      state.tabAlerts.selectedFiltersPerCategory[categoryKey] = updateFiltersPerCategory<AlertsFilterCategory>(
        state.tabAlerts.selectedFiltersPerCategory[categoryKey] || {},
        action.payload,
      );
      state.isPolling = false;
    },

    tabAlertsClearAllFiltersAction(state) {
      state.tabAlerts.selectedFiltersPerCategory = {};
      state.isPolling = false;
    },

    tabAttacksSetSearchValueAction(state, action: PayloadAction<string>) {
      state.tabAttacks.searchValue = action.payload;
      state.isPolling = false;
    },

    tabInventoryToggleIsSidebarFilterOpenAction(state, action: PayloadAction<boolean | undefined>) {
      state.tabInventory.isSidebarFilterOpen =
        action.payload === undefined ? !state.tabInventory.isSidebarFilterOpen : action.payload;
    },

    tabAttacksToggleIsSidebarFilterOpenAction(state, action: PayloadAction<boolean | undefined>) {
      state.tabAttacks.isSidebarFilterOpen =
        action.payload === undefined ? !state.tabAttacks.isSidebarFilterOpen : action.payload;
    },

    tabAttacksUpdateFiltersPerCategoryAction(
      state,
      action: PayloadAction<UpdateFiltersPerCategoryArgs<AttackFilterField>>,
    ) {
      const categoryKey = action.payload.categoryKey;

      state.tabAttacks.selectedFiltersPerCategory[categoryKey] = updateFiltersPerCategory<AttackFilterField>(
        state.tabAttacks.selectedFiltersPerCategory[categoryKey] || {},
        action.payload,
      );
      state.isPolling = false;
    },

    tabAttacksClearAllFiltersAction(state) {
      state.tabAttacks.selectedFiltersPerCategory = {};
      state.isPolling = false;
    },

    tabAnomaliesSetSearchValueAction(state, action: PayloadAction<string>) {
      state.tabAnomalies.searchValue = action.payload;
      state.isPolling = false;
    },

    tabAnomaliesToggleIsSidebarFilterOpenAction(state, action: PayloadAction<boolean | undefined>) {
      state.tabAnomalies.isSidebarFilterOpen =
        action.payload === undefined ? !state.tabAnomalies.isSidebarFilterOpen : action.payload;
    },

    tabAnomaliesUpdateFiltersPerCategoryAction(
      state,
      action: PayloadAction<UpdateFiltersPerCategoryArgs<AnomaliesFilterCategory>>,
    ) {
      const categoryKey = action.payload.categoryKey;

      state.tabAnomalies.selectedFiltersPerCategory[categoryKey] = updateFiltersPerCategory<AnomaliesFilterCategory>(
        state.tabAnomalies.selectedFiltersPerCategory[categoryKey] || {},
        action.payload,
      );
      state.isPolling = false;
    },

    tabAnomaliesClearAllFiltersAction(state) {
      state.tabAnomalies.selectedFiltersPerCategory = {};
      state.isPolling = false;
    },

    tabLogsToggleRowAction: function (state, action: PayloadAction<string>) {
      const rowId = action.payload;
      const isRowExpanded = state.tabLogs.expandedRows[rowId];

      if (isRowExpanded) {
        delete state.tabLogs.expandedRows[rowId];
      } else {
        state.tabLogs.expandedRows[action.payload] = true;
      }

      state.isPolling = state.isPolling && !Object.values(state.tabLogs.expandedRows).some((expanded) => expanded);
    },

    tabLogsCollapseAllAction(state) {
      state.tabLogs.expandedRows = {};
    },

    tabLogsToggleIsFiltersPanelOpen(state) {
      state.tabLogs.isFiltersPanelOpen = !state.tabLogs.isFiltersPanelOpen;
    },

    clearFleetsViewStateAction() {
      return getInitialState();
    },
  },
});

export const {
  toggleIsPollingAction,
  tabAlertsSetSearchValueAction,
  tabAlertsToggleIsSidebarFilterOpenAction,
  tabAlertsUpdateFiltersPerCategoryAction,
  tabAlertsClearAllFiltersAction,
  tabAttacksSetSearchValueAction,
  tabInventoryToggleIsSidebarFilterOpenAction,
  tabAttacksToggleIsSidebarFilterOpenAction,
  tabAttacksUpdateFiltersPerCategoryAction,
  tabAttacksClearAllFiltersAction,
  tabAnomaliesSetSearchValueAction,
  tabAnomaliesToggleIsSidebarFilterOpenAction,
  tabAnomaliesUpdateFiltersPerCategoryAction,
  tabAnomaliesClearAllFiltersAction,
  tabLogsCollapseAllAction,
  tabLogsToggleIsFiltersPanelOpen,
  tabLogsToggleRowAction,
  clearFleetsViewStateAction,
} = fleetsViewSlice.actions;
